// The tag-category grid of Developer › Integrations (backend plan 2026-10-29 sdtc WP-G): the words for its statuses
// and origins, and the default row's settings as the flag dialog edits them. The flag set, its descriptions and its
// rules are Trip defaults' (components/TagCategoryTableView.js `operatorDefaultFlagColumns` and its disabling rules,
// components/TripDefaultSettings2.js's checkbox handler), so the two editors of the same rows cannot drift.
import { operatorDefaultFlagColumns } from '../../components/TagCategoryTableView';
import {
    DEPARTURE_KEY_LABEL,
    OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS,
    TAG_CATEGORY_BOOL_SETTINGS,
} from '../../utils/tagCategories';
import type { Label } from '../labels';
import type { TagCategoryDefault, TagCategoryEmit, TagCategoryGridRow, TagCategoryStatus } from '../integrationTypes';

/** The 19 association flags of a default row (`departure_key` is on operator defaults only). */
export const DEFAULT_ROW_FLAGS: string[] = [...TAG_CATEGORY_BOOL_SETTINGS, ...OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS];
export const COUNT_FIELDS = ['min_tags', 'max_tags'];
/** What Trip defaults writes for "Allow more than one tag" (the field's upper bound). */
export const MAX_TAG_COUNT = 32767;

/** A default row's settings, flat: `min_tags`, `max_tags` and the 19 flags. */
export type DefaultValues = Record<string, boolean | number>;

/** The dialog's checkboxes, in Trip defaults' order. `required`, `max_tags` and `allow_multiple_tags` are read from
 *  and written to the counts (min_tags > 0; max_tags 1; max_tags > 1). */
export const FLAG_FIELDS: Array<{ id: string; label: string; tooltip: string }> = operatorDefaultFlagColumns;

const COUNT_LABELS: Record<string, string> = {
    required: 'Required',
    max_tags: 'Single tag',
    allow_multiple_tags: 'Multiple tags',
};

/** A field's short name: the server's `flag_labels` (the console's words), Trip defaults' "Departure identity" for
 *  `departure_key`, and the count fields' own. */
export const flagLabel = (id: string, flagLabels: Record<string, string> = {}) => {
    if (id === 'departure_key') {
        return DEPARTURE_KEY_LABEL;
    }
    if (id === 'min_tags') {
        return COUNT_LABELS.required;
    }
    return COUNT_LABELS[id] || flagLabels[id] || id.replace(/_/g, ' ');
};

/** Every field off: what an undeclared category's Add starts from. */
export const blankValues = (): DefaultValues => ({
    min_tags: 0,
    max_tags: 0,
    ...Object.fromEntries(DEFAULT_ROW_FLAGS.map((name) => [name, false])),
});

/** An Add's starting point: the declaration's seed over all off (a seed lists only what is on). */
export const valuesFromSeed = (seed: TagCategoryGridRow['seed']): DefaultValues => {
    const values = blankValues();
    Object.entries(seed || {}).forEach(([name, value]) => {
        if (COUNT_FIELDS.includes(name)) {
            values[name] = Number(value) || 0;
        } else if (DEFAULT_ROW_FLAGS.includes(name)) {
            values[name] = !!value;
        }
    });
    return values;
};

/** A stored default as the dialog edits it. */
export const valuesFromDefault = (row: TagCategoryDefault): DefaultValues => ({
    min_tags: row.min_tags,
    max_tags: row.max_tags,
    ...Object.fromEntries(DEFAULT_ROW_FLAGS.map((name) => [name, !!row.flags[name]])),
});

/** The PATCH body's fields: `{from, to}` for each field that changed, nothing else (compare-and-set). */
export const changedFields = (before: DefaultValues, after: DefaultValues) => Object.fromEntries(
    [...COUNT_FIELDS, ...DEFAULT_ROW_FLAGS]
        .filter((name) => before[name] !== after[name])
        .map((name) => [name, { from: before[name], to: after[name] }]),
);

/** A checkbox ticked or cleared, with Trip defaults' linked changes: Guides and GPS / users exclude each other, and
 *  a flag that needs another goes off with it (its checkbox is then disabled, so it could not be cleared any more).
 *  Only the touched field's dependants move: a stored row that already breaks a rule is not "fixed" behind the
 *  user's back (the server's rules are no-worse). `initial` keeps a stored count when a box is ticked again. */
export const toggleField = (values: DefaultValues, id: string, checked: boolean, initial: DefaultValues = blankValues()): DefaultValues => {
    const next = { ...values };
    if (id === 'required') {
        next.min_tags = checked ? (Number(initial.min_tags) > 0 ? initial.min_tags : 1) : 0;
    } else if (id === 'max_tags') {
        next.max_tags = checked ? 1 : 0;
    } else if (id === 'allow_multiple_tags') {
        next.max_tags = checked ? (Number(initial.max_tags) > 1 ? initial.max_tags : MAX_TAG_COUNT) : 0;
    } else {
        next[id] = checked;
    }
    if (id === 'guides_from_tags' && checked) {
        next.user_association = false;
    }
    if (id === 'user_association' && checked) {
        next.guides_from_tags = false;
        next.remove_unreferenced_guides = false;
    }
    if (id === 'guides_from_tags' && !checked) {
        next.remove_unreferenced_guides = false;
    }
    if (id === 'location_sharing' && !checked) {
        next.user_association = false;
    }
    if (id === 'cleanup_on_assignment' && !checked) {
        next.remove_guides_on_cleanup = false;
    }
    return next;
};

/** How one checkbox reads: Trip defaults' rules (TagCategoryTableView.js), each disabled box saying why.
 *  `checkins` / `boardings`: the other categories of the effective defaults with the flag (at most 3 and 1). */
export const fieldState = (values: DefaultValues, id: string, { code, checkins, boardings }: { code: string | null; checkins: number; boardings: number }) => {
    const maxTags = Number(values.max_tags) || 0;
    const on = (name: string) => !!values[name];
    let checked = on(id);
    let hint: string | null = null;
    if (id === 'required') {
        // A departure is always required (Trip defaults shows it so and never lets it change).
        checked = Number(values.min_tags) > 0 || code === 'departure';
        hint = code === 'departure' ? 'A departure category is always required.' : null;
    } else if (id === 'max_tags') {
        checked = maxTags === 1;
        hint = maxTags > 1 ? 'Clear “Multiple tags” first.' : null;
    } else if (id === 'allow_multiple_tags') {
        checked = maxTags > 1;
        hint = maxTags === 1 ? 'Clear “Single tag” first.' : null;
    } else if (id === 'user_association' && !on('location_sharing')) {
        hint = 'Needs “Location groups”.';
    } else if (id === 'remove_guides_on_cleanup' && !on('cleanup_on_assignment')) {
        hint = 'Needs “Clean-up”.';
    } else if (id === 'remove_unreferenced_guides' && !on('guides_from_tags')) {
        hint = 'Needs “Guides”.';
    } else if (id === 'checkin' && !on('checkin')) {
        if (on('boarding')) {
            hint = 'This category is already used for boarding — uncheck Boarding first';
        } else if (checkins >= 3) {
            hint = 'There are already 3 check-in tag categories selected';
        }
    } else if (id === 'boarding' && !on('boarding')) {
        if (on('checkin')) {
            hint = 'This category is already used for check-in — uncheck Check-in first';
        } else if (boardings >= 1) {
            hint = 'There is already 1 boarding tag category selected (on another category)';
        }
    }
    return { checked, disabled: hint !== null, hint };
};

/** What a default (or a seeded row's `fields`) has on, as chip labels in the dialog's order. */
export const onLabels = (values: DefaultValues | Record<string, boolean | number>, flagLabels: Record<string, string>) => {
    const out: string[] = [];
    if (Number(values.min_tags) > 0) {
        out.push(flagLabel('required'));
    }
    if (Number(values.max_tags) === 1) {
        out.push(flagLabel('max_tags'));
    } else if (Number(values.max_tags) > 1) {
        out.push(flagLabel('allow_multiple_tags'));
    }
    FLAG_FIELDS.filter((f) => DEFAULT_ROW_FLAGS.includes(f.id) && values[f.id]).forEach((f) => out.push(flagLabel(f.id, flagLabels)));
    return out;
};

// ---- statuses and origins ------------------------------------------------------------------------------------------

/** The grid's order: the two mismatches first (the backend's STATUS_ORDER; re-applied here, stable). */
export const STATUS_ORDER: TagCategoryStatus[] = ['needs_default', 'missing_category', 'matches', 'inactive', 'informational', 'not_emitted'];

/** A row's status in words (`{system}`: the booking system's name; developerContract.test.js holds the contract's
 *  `IntegrationTagCategoryStatus` to these keys). */
export const TAG_CATEGORY_STATUSES: Record<string, Label> = {
    needs_default: {
        label: 'No default',
        tone: 'warning',
        hint: '{system} writes these tags, but with no default they get every setting off (and are dropped where the booking declares no category).',
    },
    missing_category: {
        label: 'Category missing',
        tone: 'error',
        hint: '{system} would write these tags, but the company has no such category, so they are skipped without a word.',
    },
    matches: { label: 'Default set', tone: 'success', hint: '{system} writes these tags, and the default gives them their settings.' },
    inactive: { label: 'Not active', tone: 'neutral', hint: '{system} writes these tags only under a setting that is off now.' },
    informational: {
        label: 'Informational',
        tone: 'neutral',
        hint: 'Only the product sync, a catalogue sync or the booking itself names this category: a default is optional.',
    },
    not_emitted: { label: 'Not from {system}', tone: 'neutral', hint: 'A default {system} never writes: another integration or the dashboard may.' },
};

export const tagCategoryStatus = (status: string, system: string): Label => {
    const words = TAG_CATEGORY_STATUSES[status];
    if (!words) {
        return { label: status, tone: 'neutral' };
    }
    const fill = (text: string) => text.replace(/\{system\}/g, system);
    return { ...words, label: fill(words.label), hint: words.hint ? fill(words.hint) : undefined };
};

/** Where a category's tags come from, for the "Emitted by" chips. */
export const EMIT_ORIGINS: Record<string, string> = {
    hardcoded: 'Built in',
    dynamic: 'From the booking',
    tag_mapping: 'Tag mapping',
    preset: 'Preset',
    questions: 'Questions',
    crew_role_map: 'Crew roles',
    resources: 'Resources',
    unit_question_tag_mapping: 'Unit questions',
    note_to_tag: 'Notes',
    boards: 'Boards',
    column_to_tag_map: 'Columns',
    product_sync: 'Product sync',
    aux_catalogue: 'Catalogue sync',
    tag_category_set: 'Recommended set',
};

export const emitOrigin = (origin: string) => EMIT_ORIGINS[origin] || origin.replace(/_/g, ' ');

/** An origin chip's tooltip: the rule, the account, the condition, and what follows from it. */
export const emitHint = (emit: TagCategoryEmit) => [
    emit.detail,
    emit.account ? `Account: ${emit.account}` : null,
    emit.condition ? `When: ${emit.condition}` : null,
    emit.active ? null : 'Not active under the current settings.',
    emit.optional ? 'Optional: only some bookings carry it.' : null,
    emit.tour_tc ? null : 'The booking declares no category for it: without a default its tags are dropped.',
].filter(Boolean).join(' · ');

/** The crew row's hint (plan WP-J caveat): `guides_from_tags` assigns guides only from tags linked to a user. */
export const crewHint = (count: number | null) => (
    count ? `${count} crew ${count === 1 ? 'tag' : 'tags'} not linked to a user: guides won't be assigned` : null
);
